/**
 * AddShareModal — W4.8
 *
 * Modal zum Hinzufuegen eines Shares zu einem Contact.
 * Felder: Hotspot-Select (via useHotspots), Percentage | FixedAmount-Toggle, Tags.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHotspots } from '../hotspots/useHotspots'
import { useCreateShare, useUpdateShare } from './useShareMutations'
import type { ShareFormData } from './useShareMutations'
import type { Share } from './useContactDetail'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'

type Props = {
  contactId: string
  isOpen: boolean
  onClose: () => void
  /** Wenn gesetzt: Modal editiert diesen Share statt einen neuen anzulegen
   *  (PATCH /api/v2/shares/{id} statt POST). Hotspot + Zahlungsrichtung sind
   *  im Update-Endpoint nicht editierbar (siehe openapi.yaml) und werden im
   *  Edit-Modus daher nicht angezeigt. */
  share?: Share
}

type AmountMode = 'percentage' | 'fixed'

export function AddShareModal({ contactId, isOpen, onClose, share }: Props) {
  const { t } = useTranslation('contacts')
  const isEdit = share != null
  const [hotspotId, setHotspotId] = useState(share?.hotspot_id ?? '')
  const [amountMode, setAmountMode] = useState<AmountMode>(share?.fixed_amount != null ? 'fixed' : 'percentage')
  const [percentage, setPercentage] = useState(share?.percentage != null ? String(share.percentage) : '')
  const [fixedAmount, setFixedAmount] = useState(share?.fixed_amount != null ? String(share.fixed_amount) : '')
  const [tagListInput, setTagListInput] = useState(share?.tags?.join(', ') ?? '')
  const [startDate, setStartDate] = useState(share?.start_date ?? '')
  const [endDate, setEndDate] = useState(share?.end_date ?? '')
  const [payment, setPayment] = useState<boolean | null>(null)
  const [errors, setErrors] = useState<{ hotspot?: string; amount?: string; dates?: string; payment?: string }>({})

  const { data: hotspots, isLoading: hotspotsLoading } = useHotspots()
  const createShare = useCreateShare(contactId)
  const updateShare = useUpdateShare(contactId)
  const mutation = isEdit ? updateShare : createShare

  function validate(): boolean {
    const newErrors: { hotspot?: string; amount?: string; dates?: string; payment?: string } = {}

    if (!isEdit && !hotspotId) {
      newErrors.hotspot = t('addShareModal.validation.hotspotRequired')
    }

    if (amountMode === 'percentage') {
      const pct = parseFloat(percentage)
      if (!percentage || isNaN(pct) || pct <= 0 || pct > 100) {
        newErrors.amount = t('addShareModal.validation.percentageRequired')
      }
    } else {
      const amt = parseFloat(fixedAmount)
      if (!fixedAmount || isNaN(amt) || amt <= 0) {
        newErrors.amount = t('addShareModal.validation.fixedAmountRequired')
      }
    }

    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        newErrors.dates = t('addShareModal.validation.dateOrder')
      }
    }

    if (!isEdit && payment === null) {
      newErrors.payment = t('addShareModal.validation.paymentRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    const amountField = amountMode === 'percentage'
      ? { percentage: parseFloat(percentage) }
      : { fixed_amount: parseFloat(fixedAmount) }

    try {
      if (isEdit && share) {
        // Hotspot + Zahlungsrichtung sind hier nicht editierbar (s. Props-Kommentar).
        await updateShare.mutateAsync({
          shareId: share.id,
          data: {
            ...amountField,
            tag_list: tagListInput.trim(),
            start_date: startDate || undefined,
            end_date: endDate || undefined,
          },
        })
      } else {
        const data: ShareFormData = {
          hotspot_id: hotspotId,
          ...amountField,
          ...(tagListInput.trim() ? { tag_list: tagListInput.trim() } : {}),
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(payment !== null ? { payment } : {}),
        }
        await createShare.mutateAsync(data)
      }
      handleClose()
    } catch {
      // Fehler im UI sichtbar
    }
  }

  function handleClose() {
    // Reset auf Ausgangswerte (bei Edit: Werte des Shares, bei Create: leer) —
    // die Modal-Instanz bleibt gemountet (isOpen steuert nur die Anzeige),
    // ein reiner Blank-Reset wuerde beim naechsten Oeffnen im Edit-Modus die
    // Share-Daten verlieren.
    setHotspotId(share?.hotspot_id ?? '')
    setAmountMode(share?.fixed_amount != null ? 'fixed' : 'percentage')
    setPercentage(share?.percentage != null ? String(share.percentage) : '')
    setFixedAmount(share?.fixed_amount != null ? String(share.fixed_amount) : '')
    setTagListInput(share?.tags?.join(', ') ?? '')
    setStartDate(share?.start_date ?? '')
    setEndDate(share?.end_date ?? '')
    setPayment(null)
    setErrors({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-share-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
      data-testid="add-share-modal"
    >
      <div className="modal">
        <div className="modal-header">
          <h2 id="add-share-title">{t(isEdit ? 'addShareModal.editTitle' : 'addShareModal.title')}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('addShareModal.closeAriaLabel')}
            onClick={handleClose}
          >
            &times;
          </button>
        </div>

        <form onSubmit={(e) => { void handleSubmit(e) }}>
          <div className="modal-body">
            {/* Hotspot: im Edit-Modus nicht aenderbar (Update-Endpoint kennt kein
                hotspot_id), daher nur Anzeige statt Select. */}
            <div className="form-group">
              <label htmlFor="share-hotspot">{t('addShareModal.fields.hotspot')}</label>
              {isEdit ? (
                <input id="share-hotspot" className="input" value={share?.hotspot_name ?? ''} disabled />
              ) : (
                <>
                  <select
                    id="share-hotspot"
                    className={`input${errors.hotspot ? ' is-error' : ''}`}
                    value={hotspotId}
                    onChange={(e) => setHotspotId(e.target.value)}
                    disabled={hotspotsLoading}
                    data-testid="share-hotspot-select"
                  >
                    <option value="">
                      {hotspotsLoading ? t('addShareModal.hotspotLoading') : t('addShareModal.hotspotPlaceholder')}
                    </option>
                    {hotspots?.map((h) => (
                      <option key={h.id} value={h.id}>
                        {hotspotDisplayName(h.name, h.address, t('addShareModal.hotspotFallback', { id: h.id }))}
                      </option>
                    ))}
                  </select>
                  {errors.hotspot && (
                    <span className="field-error" role="alert">
                      {errors.hotspot}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Amount Mode Toggle */}
            <div className="form-group">
              <label>{t('addShareModal.fields.amountMode')}</label>
              <div className="chips" role="group" aria-label={t('addShareModal.amountModeAriaLabel')}>
                <button
                  type="button"
                  className={`chip${amountMode === 'percentage' ? ' is-active' : ''}`}
                  onClick={() => setAmountMode('percentage')}
                  aria-pressed={amountMode === 'percentage'}
                  data-testid="mode-percentage"
                >
                  {t('addShareModal.modePercentage')}
                </button>
                <button
                  type="button"
                  className={`chip${amountMode === 'fixed' ? ' is-active' : ''}`}
                  onClick={() => setAmountMode('fixed')}
                  aria-pressed={amountMode === 'fixed'}
                  data-testid="mode-fixed"
                >
                  {t('addShareModal.modeFixed')}
                </button>
              </div>
            </div>

            {/* Amount Input */}
            <div className="form-group">
              {amountMode === 'percentage' ? (
                <>
                  <label htmlFor="share-percentage">{t('addShareModal.fields.percentage')}</label>
                  <input
                    id="share-percentage"
                    className={`input${errors.amount ? ' is-error' : ''}`}
                    type="number"
                    min="0.01"
                    max="100"
                    step="0.01"
                    value={percentage}
                    onChange={(e) => setPercentage(e.target.value)}
                    placeholder="10.0"
                    data-testid="share-percentage-input"
                  />
                </>
              ) : (
                <>
                  <label htmlFor="share-fixed">{t('addShareModal.fields.fixedAmount')}</label>
                  <input
                    id="share-fixed"
                    className={`input${errors.amount ? ' is-error' : ''}`}
                    type="number"
                    min="0.0001"
                    step="0.0001"
                    value={fixedAmount}
                    onChange={(e) => setFixedAmount(e.target.value)}
                    placeholder="0.5000"
                    data-testid="share-fixed-input"
                  />
                </>
              )}
              {errors.amount && (
                <span className="field-error" role="alert">
                  {errors.amount}
                </span>
              )}
            </div>

            {/* Start / End Date */}
            <div className="form-group" style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="share-start-date">{t('addShareModal.fields.startDate')}</label>
                <input
                  id="share-start-date"
                  className={`input${errors.dates ? ' is-error' : ''}`}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="share-start-date-input"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="share-end-date">{t('addShareModal.fields.endDate')}</label>
                <input
                  id="share-end-date"
                  className={`input${errors.dates ? ' is-error' : ''}`}
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="share-end-date-input"
                />
              </div>
            </div>
            <p className="help-text" style={{ marginTop: '-0.5rem', fontSize: '0.85rem', color: 'var(--color-text-muted, #666)' }}>
              {t('addShareModal.dateHelpText')}
            </p>
            {errors.dates && (
              <span className="field-error" role="alert" style={{ display: 'block', marginTop: '0.25rem' }}>
                {errors.dates}
              </span>
            )}

            {/* Payment-Richtung: im Edit-Modus nicht editierbar (Update-Endpoint
                erwartet zwar ein optionales payment-Feld, der aktuelle Wert
                wird von der API aber nicht im Share zurueckgegeben — daher
                hier ausgeblendet statt eine falsche Auswahl zu erzwingen). */}
            {!isEdit && (
              <div className="form-group">
                <label>{t('addShareModal.fields.paymentDirection')}</label>
                <div className="chips" role="group" aria-label={t('addShareModal.paymentDirectionAriaLabel')}>
                  <button
                    type="button"
                    className={`chip${payment === true ? ' is-active' : ''}`}
                    onClick={() => setPayment(true)}
                    aria-pressed={payment === true}
                    data-testid="payment-out"
                  >
                    {t('addShareModal.paymentOut')}
                  </button>
                  <button
                    type="button"
                    className={`chip${payment === false ? ' is-active' : ''}`}
                    onClick={() => setPayment(false)}
                    aria-pressed={payment === false}
                    data-testid="payment-in"
                  >
                    {t('addShareModal.paymentIn')}
                  </button>
                </div>
                {errors.payment && (
                  <span className="field-error" role="alert">
                    {errors.payment}
                  </span>
                )}
              </div>
            )}

            {/* Tags */}
            <div className="form-group">
              <label htmlFor="share-tags">{t('addShareModal.fields.tags')}</label>
              <input
                id="share-tags"
                className="input"
                type="text"
                value={tagListInput}
                onChange={(e) => setTagListInput(e.target.value)}
                placeholder={t('addShareModal.placeholders.tags')}
                data-testid="share-tags-input"
              />
            </div>

            {mutation.isError && (
              <div className="field-error" role="alert">
                {t('addShareModal.saveError')}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              {t('addShareModal.cancelBtn')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={mutation.isPending}
              data-testid="add-share-submit"
            >
              {mutation.isPending
                ? t('addShareModal.submitPending')
                : t(isEdit ? 'addShareModal.editSubmitBtn' : 'addShareModal.submitBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
